import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../../i18n/apiError'
import { leadsAPI } from './api'
import Pagination from './Pagination'
import { useMineMarkedsomrader } from '../../hooks/useMineMarkedsomrader'
import type { ScoreRow } from './api'

const PAGE_SIZE = 20

export default function NaceDetailPage() {
  const { t } = useTranslation('leads')
  const { naceKode } = useParams<{ naceKode: string }>()
  // The markedsomrade arrives in the query string so a refresh or a shared
  // link keeps the scope. Without it this page queried nationally while the
  // tab that linked here was scoped, and the two counts could not be
  // reconciled — 1000+ here against 549 there for 43.210 in Oslo.
  const [searchParams] = useSearchParams()
  const markedsomradeIds = searchParams.get('mo') || undefined
  // Return to the exact leads view the user came from — tab, page and
  // filters all live in that URL. Falls back to a bare /leads for direct
  // links and refreshes, where there is no view to return to.
  const backTo = ((useLocation().state as { from?: string } | null)?.from) ?? '/leads'
  const navigate = useNavigate()
  const location = useLocation()
  const [rows, setRows] = useState<ScoreRow[]>([])
  const [total, setTotal] = useState(0)
  const [totalIsCapped, setTotalIsCapped] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [naceName, setNaceName] = useState<string | null>(null)

  useEffect(() => {
    if (!naceKode) return
    setLoading(true)
    setError(null)
    leadsAPI.getScores({
      nace_kode: naceKode,
      markedsomrade_ids: markedsomradeIds,
      sort_by: 'total_score',
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    })
      .then(res => {
        setRows(res.data.items)
        setTotal(res.data.total)
        setTotalIsCapped(res.data.total_is_capped)
        setHasMore(res.data.has_more)
        if (res.data.items.length > 0 && res.data.items[0].nace_beskrivelse) {
          setNaceName(res.data.items[0].nace_beskrivelse)
        }
      })
      .catch(err => setError(localizeApiDetail(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false))
  }, [naceKode, page, markedsomradeIds])

  useEffect(() => { setPage(1) }, [markedsomradeIds])

  const { items: markedsomrader } = useMineMarkedsomrader()
  const markedsomradeName = markedsomradeIds
    ? markedsomrader.find(m => String(m.id) === markedsomradeIds)?.name
    : undefined


  return (
    <div className="space-y-4">
      <Link to={backTo} className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline text-sm">
        <ArrowLeft className="w-4 h-4" /> {t('naceDetail.back')}
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
        NACE {naceKode}{naceName ? ` — ${naceName}` : ''}
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {markedsomradeName
          ? t('naceDetail.inArea', { area: markedsomradeName })
          : t('naceDetail.national')}
        {' '}{t('naceDetail.listInfo')}
        {!loading && !error && (
          <> {t('naceDetail.hits', { n: totalIsCapped ? '1000+' : total.toLocaleString('nb-NO') })}</>
        )}
      </p>

      {error && <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>}

      <div className="card p-6">
        {loading ? (
          <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
        ) : rows.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('naceDetail.empty')}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-left">{t('common.bedrift')}</th>
                    <th className="text-left">{t('common.orgNr')}</th>
                    <th className="text-right">{t('common.score')}</th>
                    <th className="text-right">{t('naceDetail.kjoretoyTotalt')}</th>
                    <th className="text-right" title={t('naceDetail.eideTitle')}>{t('naceDetail.eide')}</th>
                    <th className="text-right" title={t('naceDetail.leasedeTitle')}>{t('naceDetail.leasede')}</th>
                    <th className="text-right">{t('common.personbil')}</th>
                    <th className="text-right">{t('common.varebil')}</th>
                    <th className="text-right">{t('naceDetail.bilerMedLead')}</th>
                    <th className="text-left">{t('naceDetail.hottesteKategori')}</th>
                    <th className="text-left">{t('common.kommune')}</th>
                    <th className="text-center">{t('common.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.org_nr}
                        className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors`}>
                      <td className="font-bold">{row.navn ?? '\u2014'}</td>
                      <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{row.org_nr}</td>
                      <td className="text-right font-bold">{row.total_score}</td>
                      <td className="text-right">{(row.total_vehicles ?? 0).toLocaleString('nb-NO')}</td>
                      <td className="text-right">{(row.owned_cars ?? 0).toLocaleString('nb-NO')}</td>
                      <td className="text-right">{(row.leased_cars ?? 0).toLocaleString('nb-NO')}</td>
                      <td className="text-right">{(row.personbil_count ?? 0).toLocaleString('nb-NO')}</td>
                      <td className="text-right">{(row.varebil_count ?? 0).toLocaleString('nb-NO')}</td>
                      <td className="text-right">{row.eligible_car_count}</td>
                      <td>
                        {row.hottest_category && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                            {row.hottest_category}
                          </span>
                        )}
                      </td>
                      <td className="text-sm">{row.kommune ?? '\u2014'}</td>
                      <td className="text-center">
                        <button onClick={() => navigate(`/leads/bedrift/${row.org_nr}`, { state: { from: location.pathname + location.search } })}
                          className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline">
                          <span className="text-xs font-medium">{t('common.see')}</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              onPageChange={setPage}
              total={total}
              totalIsCapped={totalIsCapped}
              hasMore={hasMore}
              pageSize={PAGE_SIZE}
            />
          </>
        )}
      </div>
    </div>
  )
}
